import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { asset } from '../../services/assetResolverService';

export interface AdblockModalProps {
  onDismiss: (method: 'continue_without' | 'overlay_click' | 'escape_key') => void;
  onCtaClick: (action: 'unblock' | 'premium' | 'login') => void;
  unblockUrl: string;
  premiumUrl: string;
  loginUrl: string;
  showLoginLink: boolean;
}

export const AdblockModal: FC<AdblockModalProps> = ({
  onDismiss,
  onCtaClick,
  unblockUrl,
  premiumUrl,
  loginUrl,
  showLoginLink
}) => {
  const { t } = useTranslation('upsells');
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    overlayRef.current?.focus();
  }, []);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onDismiss('overlay_click');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onDismiss('escape_key');
    }
  };

  return (
    <div
      ref={overlayRef}
      className="adblock-modal__overlay"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="adblock-modal-title"
      tabIndex={-1}
    >
      <div className="adblock-modal">
        <div className="adblock-modal__content">
          <div
            className="adblock-modal__texture"
            style={{ backgroundImage: `url(${asset('/images/bgtiles/stone.jpg')})` }}
            aria-hidden="true"
          />
          <div className="adblock-modal__glow" aria-hidden="true" />

          <div className="adblock-modal__logo">
            <img src={asset('/images/brand/Mark_2ColorWhite.svg')} alt="Raider.IO" />
          </div>

          <h2 id="adblock-modal-title" className="adblock-modal__title">
            {t('adblock-modal.title')}
          </h2>

          <p className="adblock-modal__subtitle">{t('adblock-modal.subtitle')}</p>

          <p className="adblock-modal__body">{t('adblock-modal.body')}</p>

          <div className="adblock-modal__ctas">
            <a className="adblock-modal__cta adblock-modal__cta--premium" href={premiumUrl} onClick={() => onCtaClick('premium')}>
              <span className="adblock-modal__cta-label">
                <i className="fa-solid fa-crown adblock-modal__cta-icon" aria-hidden="true" />
                {t('adblock-modal.cta-premium')}
              </span>
              <span className="adblock-modal__cta-subtitle">{t('adblock-modal.cta-premium-subtitle')}</span>
            </a>

            <a
              className="adblock-modal__cta adblock-modal__cta--disable"
              href={unblockUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onCtaClick('unblock')}
            >
              <span className="adblock-modal__cta-label">{t('adblock-modal.cta-disable')}</span>
              <span className="adblock-modal__cta-subtitle">{t('adblock-modal.cta-disable-subtitle')}</span>
            </a>
          </div>

          <div className="adblock-modal__links">
            <a
              className="adblock-modal__link"
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onDismiss('continue_without');
              }}
            >
              {t('adblock-modal.link-continue')}
            </a>

            {showLoginLink && (
              <a className="adblock-modal__link adblock-modal__link--login" href={loginUrl} onClick={() => onCtaClick('login')}>
                {t('adblock-modal.link-login')}
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
